import { useState } from 'react';
import { useAccountRoute } from '../../lib/subroute.js';
import { useRica } from '../../lib/api/hooks-rica.js';
import { useSession } from '../../lib/auth/session.js';
import {
  useBillingAccount, useMovements, useAdjustBalance, useRateDecks, useRateRules, useRateTest,
  useRateDeckVersions,
} from '../../lib/api/hooks-billing.js';
import { RateRow, AddRate, DraftBanner } from './RateEditor.js';
import { RateSheetsList, sheetSlugs } from './RateSheetsList.js';
import { UploadCarrierSheet, RebuildRetail, PublishReview } from './RateSheetTools.js';
import { Stat, StatGroup, TableSkeleton, Empty, ErrorBanner, Badge, Tabs, Tab, CardHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { formatMoney, formatDateTime, formatNumber } from '../../lib/format.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { useSubroute, childSubroute, SubrouteProvider } from '../../lib/subroute.js';
import { BuyAirtime, PaymentsTab } from './Payments.js';
import { BillingEmails } from './BillingEmails.js';
import { InvoicesTab } from './InvoicesTab.js';
import { StatementsTab } from './StatementsTab.js';
import { InvoiceDetailsCard } from './InvoiceDetailsCard.js';
import { MonthlyChargesCard } from './MonthlyCharges.js';
import { OverdueSetting } from './OverdueSetting.js';
import { AutoTopupCard } from './AutoTopupCard.js';

/** Each tab is an address: /my/billing/payments is where PayFast sends a payer back to. */
const TABS = ['overview', 'invoices', 'statements', 'payments', 'ledger', 'rates'] as const;
type BillingTab = (typeof TABS)[number];

export function BillingPage({ tenantId: propTenantId, embedded }: {
  tenantId?: string;
  embedded?: boolean;
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const tenantId = propTenantId ?? activeTenantId;
  const can = useSession((s) => s.can);
  const route = useSubroute();
  const tab: BillingTab = TABS.find((t) => t === route.rest[0]) ?? 'overview';
  const setTab = (t: BillingTab) => route.go(t === 'overview' ? [] : [t]);

  const account = useBillingAccount(tenantId);
  /**
   * Staff or the account's own people, from who is signed in -- not from where the page is shown.
   * Inside the client portal (embedded) clients were treated as staff, which hid Buy airtime and
   * their own auto top-up (UX review, 21 October 2026).
   */
  const isAccount = useSession((s) => s.session?.scope.level === 'tenant' || s.session?.scope.level === 'site');
  const showRates = can('ratedeck:read');

  return (
    <>
      {(!embedded || isAccount) && (
        <div className="arc-page-head">
          <div>
            <h1 className="arc-h1">Billing</h1>
            <p className="arc-muted" style={{ marginTop: 3 }}>
              Balance, usage charges and rates
            </p>
          </div>
        </div>
      )}

      <Tabs>
        <Tab active={tab === 'overview'} onSelect={() => setTab('overview')}>Overview</Tab>
        {can('invoice:read') && (
          <Tab active={tab === 'invoices'} onSelect={() => setTab('invoices')}>Invoices</Tab>
        )}
        <Tab active={tab === 'statements'} onSelect={() => setTab('statements')}>Statements</Tab>
        <Tab active={tab === 'payments'} onSelect={() => setTab('payments')}>Payments</Tab>
        <Tab active={tab === 'ledger'} onSelect={() => setTab('ledger')}>Ledger</Tab>
        {showRates && (
          <Tab active={tab === 'rates'} onSelect={() => setTab('rates')}>Rates</Tab>
        )}
      </Tabs>

      {/* Buying is the client's own: staff looking at an account get its payments, not a checkout. */}
      {tab === 'overview' && (
        <Overview tenantId={tenantId} account={account} canBuy={isAccount && can('payment:make')}
          staff={!isAccount} />
      )}
      {tab === 'invoices' && (
        <SubrouteProvider value={childSubroute(route, ['invoices'])}>
          <InvoicesTab tenantId={tenantId} />
        </SubrouteProvider>
      )}
      {tab === 'statements' && (
        <SubrouteProvider value={childSubroute(route, ['statements'])}>
          <StatementsTab tenantId={tenantId} />
        </SubrouteProvider>
      )}
      {tab === 'payments' && (
        <SubrouteProvider value={childSubroute(route, ['payments'])}>
          <PaymentsTab tenantId={tenantId} />
        </SubrouteProvider>
      )}
      {tab === 'ledger' && <Ledger tenantId={tenantId} />}
      {tab === 'rates' && showRates && <Rates />}
    </>
  );
}

function Overview({ tenantId, account, canBuy, staff }: {
  tenantId: string | null;
  account: ReturnType<typeof useBillingAccount>;
  canBuy: boolean;
  /** Staff looking at an account, who can fix what stops it being billed. */
  staff: boolean;
}) {
  const can = useSession((s) => s.can);
  const [adjusting, setAdjusting] = useState(false);
  const [buying, setBuying] = useState(false);
  // Airtime waits for RICA approval (0170): said beside the button rather than as a refusal after it.
  const rica = useRica(tenantId, canBuy && can('rica:read'));
  const ricaBlocked = !!rica.data && rica.data.status !== 'approved';
  const account_ = useAccountRoute();

  if (account.isLoading) {
    return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 80 }} /></div>;
  }

  // A 404 here is a real answer, not an error: the tenant has no billing
  // account yet. Saying so beats showing a scary failure banner.
  if (account.error instanceof ApiClientError && account.error.status === 404) {
    return (
      <div className="arc-card">
        <Empty
          icon="billing"
          title="No billing account"
          hint="This account has no billing account yet. Until one exists, calls cannot be rated or charged."
        />
      </div>
    );
  }
  if (account.error) return <ErrorBanner error={account.error} />;

  const a = account.data;
  if (!a) return null;

  const balance = Number(a.balance);
  const low = a.low_balance_threshold ? Number(a.low_balance_threshold) : null;
  const tone = balance <= 0 ? 'danger' : low !== null && balance < low ? 'warn' : undefined;

  return (
    <>
      {tone === 'danger' && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }}>
          <Icon name="warning" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>No available balance</div>
            <div>On a prepaid account, outbound calls are refused once the balance reaches zero.</div>
          </div>
        </div>
      )}
      {tone === 'warn' && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 16 }}>
          <Icon name="warning" size={14} />
          <span>Balance is below the {formatMoney(a.low_balance_threshold, a.currency)} alert threshold.</span>
        </div>
      )}

      <div style={{ marginBottom: 18 }}>
        <StatGroup>
          <Stat bare label="Balance" value={formatMoney(a.balance, a.currency)} tone={tone} sub={a.mode} />
          <Stat bare label="Reserved" value={formatMoney(a.reserved, a.currency)} sub="held for calls in progress" />
          <Stat bare label="Available" value={formatMoney(a.available, a.currency)} sub="balance minus reserved" />
          <Stat bare
            label="Credit limit"
            value={formatMoney(a.credit_limit, a.currency)}
            sub={a.mode === 'prepaid' ? 'not used on prepaid' : 'postpaid headroom'}
          />
        </StatGroup>
      </div>

      <div className="arc-card">
        <CardHead title="Account" action={
          <div className="arc-row" style={{ gap: 8 }}>
            {canBuy && a.mode === 'prepaid' && ricaBlocked && (
              <span className="arc-subtle" style={{ fontSize: 12.5 }}>
                Airtime opens once {account_ ? <a href={account_.href(['rica'])} className="arc-link-btn"
                  onClick={(e) => { e.preventDefault(); account_.go(['rica']); }}>RICA verification</a> : 'RICA verification'} is approved
              </span>
            )}
            {canBuy && a.mode === 'prepaid' && !ricaBlocked && (
              <button disabled={buying} type="button" className="arc-btn arc-btn-sm arc-btn-primary"
                onClick={() => { setBuying((v) => !v); setAdjusting(false); }}>
                Buy airtime
              </button>
            )}
            {can('balance:adjust') && (
              <button disabled={adjusting} type="button" className="arc-btn arc-btn-sm"
                onClick={() => { setAdjusting((v) => !v); setBuying(false); }}>
                Adjust balance
              </button>
            )}
          </div>} />
        <div className="arc-card-body">
          {buying && tenantId && <BuyAirtime tenantId={tenantId} onCancel={() => setBuying(false)} />}
          {adjusting && tenantId && <AdjustForm tenantId={tenantId} onDone={() => setAdjusting(false)} />}
          <dl className="arc-kv">
            <dt>Mode</dt><dd>{a.mode}</dd>
            <dt>Currency</dt><dd>{a.currency}</dd>
            <dt>VAT rate</dt><dd>{(Number(a.vat_rate) * 100).toFixed(0)}%</dd>
            <dt>Billing day</dt><dd>{a.billing_day} of each month</dd>
            <dt>Status</dt><dd>{a.status}</dd>
            {staff && tenantId && <OverdueSetting tenantId={tenantId} account={a} />}
          </dl>
        </div>
      </div>

      {tenantId && <MonthlyChargesCard tenantId={tenantId} staff={staff} />}
      {tenantId && a.mode === 'prepaid' && <AutoTopupCard tenantId={tenantId} staff={staff} />}
      {tenantId && a.mode === 'prepaid' && <BillingEmails tenantId={tenantId} currency={a.currency} />}
      {tenantId && <InvoiceDetailsCard tenantId={tenantId} />}
    </>
  );
}

function AdjustForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const adjust = useAdjustBalance(tenantId);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  /**
   * Adjusting a balance by hand is step-up gated (doc 06 section 4.4). This
   * form used to print "Re-verify your identity to continue" and offer no way
   * to do it — a dead end, and the only reason nobody hit it was that logging
   * in stamped the window for free. It asks properly now, with the same
   * component SipCredential uses.
   */
  const step = useStepUp();

  const submit = () =>
    void step.run(() => adjust.mutateAsync({ amount, kind: 'topup', reason }))
      .then((r) => { if (r !== undefined) { setAmount(''); setReason(''); onDone(); } });

  return (
    <form
      style={{ marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid var(--arc-border)' }}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      <StepUpPrompt step={step} what="Adjusting a balance by hand">
        It cannot be done while impersonating another user.
      </StepUpPrompt>

      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, alignItems: 'start' }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="adj-amount">Amount</label>
          <input id="adj-amount" className="arc-input" inputMode="decimal" placeholder="500.00"
            value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <div className="arc-hint">Positive credits, negative debits.</div>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="adj-reason">Reason</label>
          <input id="adj-reason" className="arc-input" placeholder="EFT received, ref 88213"
            value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} />
          <div className="arc-hint">Stored on the ledger and shown in the customer&apos;s audit view.</div>
        </div>
        <div style={{ paddingTop: 25 }}>
          <button type="submit" className="arc-btn arc-btn-primary" disabled={adjust.isPending}>
            {adjust.isPending ? 'Posting…' : 'Post'}
          </button>
          <button type="button" className="arc-btn" style={{ marginLeft: 8 }} onClick={onDone}>Cancel</button>
        </div>
      </div>
    </form>
  );
}

function Ledger({ tenantId }: { tenantId: string | null }) {
  const movements = useMovements(tenantId);
  const tz = 'Africa/Johannesburg';

  return (
    <div className="arc-card">
      <CardHead title="Movements" sub="Append-only ledger" />
      <div className="arc-table-wrap">
        <table className="arc-table">
          <thead>
            <tr><th>Date</th><th>Type</th><th>Description</th><th className="arc-td-right">Amount</th></tr>
          </thead>
          {movements.data === undefined ? <TableSkeleton query={movements} cols={4} /> : (
            <tbody>
              {movements.data?.data.map((m) => {
                const negative = m.amount.startsWith('-');
                return (
                  <tr key={m.id}>
                    <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(m.created_at, tz)}
                    </td>
                    <td>{m.kind.replace('_', ' ')}</td>
                    <td className="arc-muted">{m.description ?? '—'}</td>
                    <td className="arc-td-right arc-num" style={{ color: negative ? 'var(--arc-danger)' : 'var(--arc-ok)', fontWeight: 550 }}>
                      {formatMoney(m.amount, m.currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      </div>
      {movements.data !== undefined && movements.data?.data.length === 0 && (
        <Empty icon="billing" title="No movements yet" hint="Charges and payments appear here." />
      )}
    </div>
  );
}

/**
 * The rate lookup tester (doc 07 section 4.10).
 *
 * "Small and disproportionately valuable" -- it settles a billing dispute in a
 * minute rather than an afternoon, because it shows which rule matched and
 * every step of the arithmetic. It calls the same engine the switch rates with.
 */
/**
 * Also mounted as the standalone /products/rate-sheets page. One component, so
 * the rate tester behaves identically wherever it is reached from.
 */
export function RateSheetsPage() {
  const route = useSubroute();
  const decks = useRateDecks();
  const slug = route.rest[0];
  if (!slug) return <RateSheetsList onOpen={(s) => route.go([s])} />;

  const slugs = sheetSlugs(decks.data?.data ?? []);
  const deck = (decks.data?.data ?? []).find((d) => slugs.get(d.id) === slug) ?? null;
  return (
    <>
      <button type="button" className="arc-crumb" onClick={() => route.go([])}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={15} /> Rate sheets
      </button>
      {decks.data && !deck && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} /><span>There is no rate sheet called {slug}.</span>
        </div>
      )}
      {deck && (
        <>
          <div className="arc-page-head">
            <div>
              <h1 className="arc-h1">{deck.name}</h1>
              <p className="arc-muted" style={{ marginTop: 3 }}>
                {deck.deck_type === 'sell' ? 'Sell sheet' : 'Cost sheet'} · {deck.currency}
                {deck.plan_count ? ` · used by ${deck.plan_count} call plan${deck.plan_count === 1 ? '' : 's'}` : ''}
              </p>
            </div>
          </div>
          <Rates deckId={deck.id} />
        </>
      )}
    </>
  );
}

/**
 * One sheet's rates and the tester. Given a sheet, it shows that one and nothing else; without one
 * (the billing tab) it offers the choice, as it always has.
 */
function Rates({ deckId: fixed }: { deckId?: string } = {}) {
  const can = useSession((s) => s.can);
  const decks = useRateDecks();
  const [deckId, setDeckId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const active = fixed ?? deckId ?? decks.data?.data[0]?.id ?? null;
  /**
   * Which version is on screen: what is live by default, the draft when asked for.
   *
   * A rate that has been edited and not published is not what the switch is charging, and a page
   * that showed the draft as though it were live would have somebody quoting a price nobody is
   * paying (owner, 25 September 2026: "can we make the rates editable?").
   */
  const [viewing, setViewing] = useState<number | null>(null);
  const versions = useRateDeckVersions(active);
  const draft = versions.data?.data.find((v) => !v.is_published) ?? null;
  const published = versions.data?.data.find((v) => v.is_published) ?? null;
  const rules = useRateRules(active, q, viewing);
  const test = useRateTest(active);
  const currency = decks.data?.data.find((d) => d.id === active)?.currency ?? 'ZAR';
  const mayEdit = can('ratedeck:write');
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  /** The one rate-sheet panel open above the table: a carrier upload, a retail rebuild, or the review. */
  const [tool, setTool] = useState<'upload' | 'rebuild' | 'review' | null>(null);
  const deck = decks.data?.data.find((d) => d.id === active) ?? null;

  const [destination, setDestination] = useState('+27821234567');
  const [duration, setDuration] = useState('60');

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 16 }}>
        <CardHead title="Rate lookup tester" sub="Same engine the switch rates with." />
        <div className="arc-card-body">
          <form
            style={{ display: 'grid', gridTemplateColumns: fixed ? '1fr 140px auto' : '1fr 140px 140px auto', gap: 12, alignItems: 'start' }}
            onSubmit={(e) => {
              e.preventDefault();
              test.mutate({ destination, duration_sec: Number(duration) || 60 });
            }}
          >
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rt-dest">Destination</label>
              <input id="rt-dest" className="arc-input" value={destination}
                onChange={(e) => setDestination(e.target.value)} placeholder="+27821234567" />
            </div>
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rt-dur">Duration (s)</label>
              <input id="rt-dur" className="arc-input" inputMode="numeric" value={duration}
                onChange={(e) => setDuration(e.target.value)} />
            </div>
            {!fixed && (
            <div className="arc-field" style={{ margin: 0 }}>
              <label className="arc-label" htmlFor="rt-deck">Deck</label>
              <select id="rt-deck" className="arc-select" value={active ?? ''} onChange={(e) => setDeckId(e.target.value)}>
                {decks.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            )}
            <div style={{ paddingTop: 25 }}>
              <button type="submit" className="arc-btn arc-btn-primary" disabled={test.isPending}>
                {test.isPending ? '…' : 'Test'}
              </button>
            </div>
          </form>

          {test.data && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--arc-border)' }}>
              {test.data.ok ? (
                <>
                  <div className="arc-row" style={{ gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div className="arc-stat-label">Charge</div>
                      <div className="arc-stat-value" style={{ fontSize: 30 }}>
                        {formatMoney(test.data.charge, test.data.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="arc-stat-label">Billable</div>
                      <div className="arc-stat-value" style={{ fontSize: 30 }}>{test.data.billable_seconds}s</div>
                    </div>
                  </div>
                  <dl className="arc-kv">
                    <dt>Matched prefix</dt>
                    <dd className="arc-mono">{test.data.breakdown?.matched_prefix}</dd>
                    <dt>Time band</dt><dd>{test.data.breakdown?.band}</dd>
                    <dt>Rate per minute</dt>
                    <dd>{formatMoney(test.data.breakdown?.rate_per_minute ?? null, test.data.currency)}</dd>
                    <dt>Connection fee</dt>
                    <dd>{formatMoney(test.data.breakdown?.connection_fee ?? null, test.data.currency)}</dd>
                    <dt>Raw / billable seconds</dt>
                    <dd className="arc-num">{test.data.breakdown?.raw_seconds}s → {test.data.breakdown?.billable_seconds}s</dd>
                    <dt>Minimum charge applied</dt>
                    <dd>{test.data.breakdown?.minimum_charge_applied ? 'yes' : 'no'}</dd>
                  </dl>
                </>
              ) : (
                <div className="arc-banner arc-banner-warn">
                  <Icon name="warning" size={14} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{test.data.reason}</div>
                    <div style={{ marginTop: 3, fontSize: 12.5 }}>
                      An unrated destination is refused rather than billed at zero — letting it
                      through free is how you find out from the carrier invoice a month later.
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {active && (
        <DraftBanner draft={draft} published={published}
          rules={rules.data?.total ?? 0} viewing={viewing} onView={setViewing}
          onReview={() => setTool('review')} />
      )}

      {active && tool === 'review' && draft && <PublishReview deckId={active} onClose={() => setTool(null)} />}
      {active && tool === 'upload' && (
        <UploadCarrierSheet deckId={active} currency={currency} onDone={() => { setTool(null); setViewing(null); }} />
      )}
      {active && tool === 'rebuild' && decks.data && (
        <RebuildRetail deckId={active} currency={currency} decks={decks.data.data} onDone={() => setTool(null)} />
      )}

      {adding && active && (
        <AddRate deckId={active} currency={currency} onDone={() => setAdding(false)} />
      )}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <input className="arc-input" placeholder="Search destination or prefix" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Filter rate rules" />
          </div>
          {mayEdit && active && (
            <button disabled={adding} type="button" className="arc-btn arc-btn-sm" onClick={() => setAdding((v) => !v)}>
              <Icon name="plus" size={13} /> New rate
            </button>
          )}
          {mayEdit && deck?.deck_type === 'cost' && (
            <button type="button" className="arc-btn arc-btn-sm" disabled={tool === 'upload'} onClick={() => setTool('upload')}>
              <Icon name="upload" size={13} /> Upload carrier sheet
            </button>
          )}
          {mayEdit && deck?.deck_type === 'sell' && (
            <button type="button" className="arc-btn arc-btn-sm" disabled={tool === 'rebuild'} onClick={() => setTool('rebuild')}>
              Rebuild from cost
            </button>
          )}
          <div className="arc-spacer" />
          {rules.data && (
            <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
              {rules.data.data.length === rules.data.total
                ? formatNumber(rules.data.total)
                : `${formatNumber(rules.data.data.length)} of ${formatNumber(rules.data.total)}`} shown
            </span>
          )}
          {rules.data && (
            <span className="arc-subtle" style={{ fontSize: 12.5 }}>
              v{rules.data.version} {rules.data.is_published
                ? <Badge tone="ok">published</Badge>
                : <Badge tone="warn">draft</Badge>}
            </span>
          )}
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Prefix</th><th>Description</th><th>Band</th>
                <th className="arc-td-right">Per minute</th>
                <th className="arc-td-right">Connect</th>
                <th className="arc-td-right">Increments</th>
                <th />
              </tr>
            </thead>
            {rules.data === undefined ? <TableSkeleton query={rules} cols={7} /> : (
              <tbody>
                {rules.data?.data.map((r) => (
                  editing === r.id && active ? (
                    <RateRow key={r.id} deckId={active} rule={r} currency={currency}
                      onDone={() => setEditing(null)} />
                  ) : (
                    <tr key={r.id}>
                      <td className="arc-mono" style={{ fontWeight: 600 }}>+{r.prefix}</td>
                      <td>{r.description ?? '—'}</td>
                      <td>{r.time_band}</td>
                      <td className="arc-td-right arc-num">{formatMoney(r.rate_per_minute, currency)}</td>
                      <td className="arc-td-right arc-num">{formatMoney(r.connection_fee, currency)}</td>
                      <td className="arc-td-right arc-num arc-subtle">
                        {r.first_increment_sec}/{r.subsequent_increment_sec}
                      </td>
                      <td className="arc-td-right">
                        {mayEdit && (
                          <RowAction kind="edit" label={`Edit +${r.prefix}`}
                            onClick={() => setEditing(r.id)} />
                        )}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            )}
          </table>
        </div>
        {rules.data !== undefined && rules.data?.data.length === 0 && (q.trim()
          ? <Empty icon="billing" title="No matching rules" hint="Try a country name or a dialling prefix." />
          : <Empty icon="billing" title="No rate rules"
              hint="Add rates here, or import a sheet. An unpriced destination is refused, never billed at zero." />
        )}
      </div>
    </>
  );
}
